// ForgotPasswordModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  requestOTPThunk,
  verifyOTPThunk,
  resetPasswordThunk,
  resendOTPThunk,
} from "../features/auth/authThunks";
import "../CSS/ForgotPasswordModal.css";

function ForgotPasswordModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  // Step management: 1 = Request OTP, 2 = Verify OTP, 3 = Reset Password, 4 = Success
  const [step, setStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    identifier: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  // UI state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Reset modal state when opened/closed
  useEffect(() => {
    if (isOpen) {
      // Modal opened - reset to initial state
      setStep(1);
      setFormData({
        identifier: "",
        otp: "",
        newPassword: "",
        confirmPassword: "",
      });
      setError(null);
      setSuccessMessage(null);
      setMaskedEmail("");
      setOtpTimer(0);
      setCanResend(false);
    }
  }, [isOpen]);

  // Timer countdown for OTP resend
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && step === 2) {
      setCanResend(true);
    }
  }, [otpTimer, step]);

  // Close modal when clicking backdrop
  const handleBackdropClick = (e) => {
    if (e.target.className === "forgot-password-modal-backdrop") {
      handleClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const handleClose = () => {
    if (step === 4) {
      // If on success step, just close
      onClose();
    } else {
      // Confirm if user wants to cancel the process
      if (step > 1) {
        if (window.confirm("Are you sure you want to cancel the password reset process?")) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!formData.identifier.trim()) {
      setError("Please enter your username or email");
      return;
    }

    try {
      const result = await dispatch(
        requestOTPThunk({ identifier: formData.identifier })
      ).unwrap();

      if (result.email_sent) {
        setMaskedEmail(result.email);
        setSuccessMessage(result.message);
        setStep(2);
        setOtpTimer(60); // Start 60 second countdown for resend
        setCanResend(false);
      }
    } catch (err) {
      setError(err || "Failed to send OTP. Please try again.");
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!formData.otp.trim()) {
      setError("Please enter the OTP");
      return;
    }

    if (formData.otp.length !== 6) {
      setError("OTP must be 6 digits");
      return;
    }

    try {
      const result = await dispatch(
        verifyOTPThunk({
          identifier: formData.identifier,
          otp: formData.otp,
        })
      ).unwrap();

      if (result.verified) {
        setSuccessMessage(result.message);
        setStep(3);
      }
    } catch (err) {
      setError(err || "Invalid OTP. Please try again.");
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      setError("Please fill in all password fields");
      return;
    }

    if (formData.newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const result = await dispatch(
        resetPasswordThunk({
          identifier: formData.identifier,
          otp: formData.otp,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword,
        })
      ).unwrap();

      if (result.success) {
        setSuccessMessage(result.message);
        setStep(4);
        
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError(err || "Failed to reset password. Please try again.");
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await dispatch(
        resendOTPThunk({ identifier: formData.identifier })
      ).unwrap();

      if (result.email_sent) {
        setSuccessMessage("OTP has been resent to your email");
        setOtpTimer(60);
        setCanResend(false);
        // Clear OTP input
        setFormData({ ...formData, otp: "" });
      }
    } catch (err) {
      setError(err || "Failed to resend OTP. Please try again.");
    }
  };

  // Reset form and go back to step 1
  const handleStartOver = () => {
    setFormData({
      identifier: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setError(null);
    setSuccessMessage(null);
    setStep(1);
    setOtpTimer(0);
    setCanResend(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="forgot-password-modal-backdrop" 
      onClick={handleBackdropClick}
    >
      <div className="forgot-password-modal">
        {/* Close Button */}
        <button 
          className="modal-close-btn" 
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 className="modal-title">Reset Password</h2>

        {/* Progress Indicator */}
        <div className="modal-progress-indicator">
          <div className={`progress-step ${step >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <div className="step-label">Request</div>
          </div>
          <div className={`progress-line ${step >= 2 ? "active" : ""}`}></div>
          <div className={`progress-step ${step >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <div className="step-label">Verify</div>
          </div>
          <div className={`progress-line ${step >= 3 ? "active" : ""}`}></div>
          <div className={`progress-step ${step >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <div className="step-label">Reset</div>
          </div>
        </div>

        <div className="modal-content">
          {/* Step 1: Request OTP */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP}>
              <p className="step-description">
                Enter your username or email address to receive a password reset OTP.
              </p>
              <div className="modal-input-container">
                <input
                  type="text"
                  name="identifier"
                  placeholder="Username or Email"
                  value={formData.identifier}
                  onChange={handleInputChange}
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="modal-submit-btn" disabled={loading}>
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP}>
              <p className="step-description">
                We've sent a 6-digit OTP to <strong>{maskedEmail}</strong>
              </p>
              <div className="modal-input-container">
                <input
                  type="text"
                  name="otp"
                  placeholder="Enter 6-digit OTP"
                  value={formData.otp}
                  onChange={handleInputChange}
                  maxLength={6}
                  pattern="[0-9]{6}"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="modal-submit-btn" disabled={loading}>
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <div className="modal-resend-section">
                {otpTimer > 0 ? (
                  <p className="resend-timer">
                    Resend OTP in {otpTimer} seconds
                  </p>
                ) : (
                  <button
                    type="button"
                    className="modal-resend-btn"
                    onClick={handleResendOTP}
                    disabled={loading || !canResend}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <p className="step-description">
                Create a new password for your account
              </p>
              <div className="modal-input-container">
                <input
                  type="password"
                  name="newPassword"
                  placeholder="New Password (min. 8 characters)"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  minLength={8}
                  required
                  autoFocus
                />
              </div>
              <div className="modal-input-container">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  minLength={8}
                  required
                />
              </div>
              <button type="submit" className="modal-submit-btn" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="modal-success-container">
              <div className="success-icon">✓</div>
              <h3>Password Reset Successful!</h3>
              <p>Your password has been reset successfully.</p>
              <p>You can now login with your new password.</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="modal-error-message">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && step !== 4 && (
            <div className="modal-success-message">
              <span className="success-icon">✓</span>
              {successMessage}
            </div>
          )}

          {/* Navigation Links */}
          {step >= 2 && step < 4 && (
            <div className="modal-navigation-links">
              <button className="modal-link-button" onClick={handleStartOver}>
                ← Start Over
              </button>
            </div>
          )}

          {/* Help Text */}
          {step < 4 && (
            <div className="modal-help-text">
              <small>
                Need help? Contact your system administrator or IT support.
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordModal;